import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  ZOOM_LEVELS, ZOOM_MAP, TASK_STATUS_COLORS, TASK_STATUS_LABELS,
  toUTC, toISO, addDays, daysBetween, dow, fmtD, fmtDY, fmtDuration, fmtVariance,
  paletteFor, assigneeLabel, hasExternal, externalLabel, EXTERNAL_BAR, SCHEDULE_STATE,
} from './planConstants';

/*
  The Gantt.

  Drawn as SVG rather than absolutely positioned divs, which is what makes the
  things Owen asked for straightforward: a summary bracket, a milestone diamond,
  four kinds of dependency arrow, and an export that produces the same picture
  you are looking at.

  The left column is HTML (it is a table, and wants to behave like one) and the
  right is one SVG per plan. Scrolling is synced between them.
*/

// Row height by density. A 200-line plan over two years is a normal size for
// this business, and at 30px a row that is 6,000px of chart to scroll through,
// so the density control is not a nicety.
const DENSITIES = { comfortable: 30, normal: 24, compact: 18 };
const HDR_H  = 44;
const DEFAULT_LEFT_W = 340;
// Rows rendered beyond the viewport, so a fast scroll does not show blank bands.
const OVERSCAN = 12;

/* ── Timescale ─────────────────────────────────────────────── */

// Tick boundaries for a given granularity, as ISO dates.
function ticksFor(granularity, from, to) {
  const out = [];
  const start = new Date(toUTC(from));
  const end = toUTC(to);

  if (granularity === 'day') {
    for (let ms = toUTC(from); ms <= end; ms += 86400000) out.push(toISO(ms));
    return out;
  }
  if (granularity === 'week') {
    // Week commencing Monday.
    const d = new Date(toUTC(from));
    const shift = (d.getUTCDay() + 6) % 7;
    d.setUTCDate(d.getUTCDate() - shift);
    while (d.getTime() <= end) { out.push(toISO(d.getTime())); d.setUTCDate(d.getUTCDate() + 7); }
    return out;
  }
  if (granularity === 'month') {
    const d = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), 1));
    while (d.getTime() <= end) { out.push(toISO(d.getTime())); d.setUTCMonth(d.getUTCMonth() + 1); }
    return out;
  }
  if (granularity === 'quarter') {
    const d = new Date(Date.UTC(start.getUTCFullYear(), Math.floor(start.getUTCMonth() / 3) * 3, 1));
    while (d.getTime() <= end) { out.push(toISO(d.getTime())); d.setUTCMonth(d.getUTCMonth() + 3); }
    return out;
  }
  const d = new Date(Date.UTC(start.getUTCFullYear(), 0, 1));
  while (d.getTime() <= end) { out.push(toISO(d.getTime())); d.setUTCFullYear(d.getUTCFullYear() + 1); }
  return out;
}

function tickLabel(granularity, iso, wide) {
  const d = new Date(toUTC(iso));
  const o = { timeZone: 'UTC' };
  switch (granularity) {
    case 'day':     return String(d.getUTCDate());
    case 'week':    return d.toLocaleDateString('en-GB', { ...o, day: 'numeric', month: wide ? 'short' : undefined });
    case 'month':   return d.toLocaleDateString('en-GB', { ...o, month: wide ? 'long' : 'short', year: wide ? 'numeric' : undefined });
    case 'quarter': return `Q${Math.floor(d.getUTCMonth() / 3) + 1}${wide ? ` ${d.getUTCFullYear()}` : ''}`;
    default:        return String(d.getUTCFullYear());
  }
}

/* ── Shapes ────────────────────────────────────────────────── */

// A summary bar is a bracket, not a block: it is the span of its children, and
// drawing it as a solid bar makes a plan look twice as full as it is.
function SummaryBracket({ x, w, y, color, critical }) {
  const h = 9;
  const cap = 5;
  const d = `M${x},${y} L${x + w},${y} L${x + w},${y + h} L${x + w - cap},${y + h - 3.5}
             L${x + cap},${y + h - 3.5} L${x},${y + h} Z`;
  return <path className="gantt-summary" d={d} fill={color} stroke={critical ? '#DC2626' : 'none'} strokeWidth={critical ? 1.2 : 0} />;
}

// Owen: "It is typical to have a diamond shape for a zero-duration completion
// milestone. Currently this appears as a 1 day blob."
function MilestoneDiamond({ x, y, size, color, done, critical }) {
  const r = size / 2;
  return (
    <path
      d={`M${x},${y - r} L${x + r},${y} L${x},${y + r} L${x - r},${y} Z`}
      fill={done ? color : '#fff'}
      stroke={critical ? '#DC2626' : color}
      strokeWidth={1.6}
    />
  );
}

/* ── Dependency arrows ─────────────────────────────────────── */

// Each link type leaves and arrives at a different corner, so the picture says
// which relationship it is without anybody having to click the task.
function arrowPath(type, from, to) {
  const { sx, sy, ex, ey } = anchors(type, from, to);
  const gap = 9;
  const midY = sy < ey ? sy + (ey - sy) / 2 : ey + (sy - ey) / 2;

  if (type === 'SS') {
    const back = Math.min(sx, ex) - gap;
    return `M${sx},${sy} H${back} V${ey} H${ex}`;
  }
  if (type === 'FF') {
    const fwd = Math.max(sx, ex) + gap;
    return `M${sx},${sy} H${fwd} V${ey} H${ex}`;
  }
  if (type === 'SF') {
    const back = sx - gap;
    const fwd = ex + gap;
    return `M${sx},${sy} H${back} V${midY} H${fwd} V${ey} H${ex}`;
  }
  // FS — the common case. Straight across if there is room, otherwise around.
  if (ex - sx >= gap * 2) {
    const mid = sx + gap;
    return `M${sx},${sy} H${mid} V${ey} H${ex}`;
  }
  const out = sx + gap;
  const back = ex - gap;
  return `M${sx},${sy} H${out} V${midY} H${back} V${ey} H${ex}`;
}

function anchors(type, from, to) {
  const fromRight = type === 'FS' || type === 'FF';
  const toRight = type === 'FF' || type === 'SF';
  return {
    sx: fromRight ? from.x2 : from.x1,
    sy: from.y,
    ex: toRight ? to.x2 : to.x1,
    ey: to.y,
  };
}

/* ── The chart ─────────────────────────────────────────────── */

export default function GanttChart({
  tasks = [],
  today,
  holidays = [],
  baseline = null,
  collapsed = {},
  onToggleCollapse,
  onEdit,
  onRescheduleTask,      // (taskId, { start_date, duration_days }) => Promise
  colourBy = 'team',
  options = {},
  onOptionsChange,
}) {
  const [zoom, setZoom] = useState('week');
  const [fitWidth, setFitWidth] = useState(null);   // px/day when "Fit" is chosen
  const [drag, setDrag] = useState(null);
  const [hover, setHover] = useState(null);
  const [density, setDensity] = useState('comfortable');
  const [leftW, setLeftW] = useState(DEFAULT_LEFT_W);
  const [fullScreen, setFullScreen] = useState(false);
  // Virtualisation: what the viewport is actually showing.
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportH, setViewportH] = useState(600);
  const [containerW, setContainerW] = useState(900);
  const rightRef = useRef(null);
  const leftRef = useRef(null);
  const svgRef = useRef(null);
  const syncing = useRef(false);
  const rowH = DENSITIES[density] || DENSITIES.comfortable;

  const {
    showBaseline = true, showCritical = true, showArrows = true,
    showProgress = true, showFloat = false,
  } = options;

  const holidaySet = useMemo(() => new Set(holidays), [holidays]);

  /* ── Range ── */
  const dates = tasks.flatMap(t => [t.start_date, t.end_date, showBaseline ? t.baseline_start : null, showBaseline ? t.baseline_finish : null]).filter(Boolean);
  const hasDates = dates.length > 0;
  const rangeFrom = useMemo(() => {
    if (!hasDates) return today;
    const min = dates.slice().sort()[0];
    // A fortnight of run-up, snapped back to a Monday, so a bar never starts
    // hard against the left edge.
    const d = addDays(min, -14);
    return addDays(d, -((dow(d) + 6) % 7));
  }, [dates.join(), today, hasDates]);
  const rangeTo = useMemo(() => {
    if (!hasDates) return addDays(today, 60);
    return addDays(dates.slice().sort().slice(-1)[0], 30);
  }, [dates.join(), today, hasDates]);

  const totalDays = Math.max(1, (daysBetween(rangeFrom, rangeTo) || 1) + 1);
  const pxPerDay = fitWidth || ZOOM_MAP[zoom].px;
  const totalW = Math.max(containerW - 4, 320, Math.round(totalDays * pxPerDay));

  // Recompute "fit" when the window changes, otherwise it goes stale on resize.
  const applyFit = useCallback(() => {
    const w = rightRef.current?.clientWidth;
    if (w) setFitWidth(Math.max(0.25, (w - 12) / totalDays));
  }, [totalDays]);
  useEffect(() => {
    if (!fitWidth) return;
    const onResize = () => applyFit();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [fitWidth, applyFit]);

  const xOf = useCallback((iso) => {
    if (!iso) return null;
    return Math.round(((daysBetween(rangeFrom, iso) || 0)) * pxPerDay * 100) / 100;
  }, [rangeFrom, pxPerDay]);
  // Inclusive finish: a bar covers its last day, so it ends at the day AFTER.
  const xEnd = useCallback((iso) => (iso ? xOf(addDays(iso, 1)) : null), [xOf]);

  /* ── Visible rows: a collapsed summary hides its subtree ── */
  const visible = useMemo(() => {
    const out = [];
    const hidden = new Set();
    for (const t of tasks) {
      if (t.parent_task_id && hidden.has(t.parent_task_id)) { hidden.add(t.id); continue; }
      out.push(t);
      if (collapsed[t.id]) hidden.add(t.id);
    }
    return out;
  }, [tasks, collapsed]);

  const rowIndex = useMemo(() => Object.fromEntries(visible.map((t, i) => [t.id, i])), [visible]);
  const totalH = Math.max(rowH, visible.length * rowH);

  /* ── Virtualisation ──
     A 200-line plan is 6,000px of chart, and drawing every bar, baseline,
     float tail and arrow for rows nobody can see is the difference between a
     chart that scrolls and one that stutters. Coordinates stay global, keyed on
     each row's index in `visible`, so only what gets RENDERED changes. */
  const firstRow = Math.max(0, Math.floor(scrollTop / rowH) - OVERSCAN);
  const lastRow = Math.min(visible.length, Math.ceil((scrollTop + viewportH) / rowH) + OVERSCAN);
  const slice = visible.slice(firstRow, lastRow);
  const inWindow = (i) => i >= firstRow && i < lastRow;

  /* ── Colour ── */
  // External tasks are always amber, so they take no place in the legend.
  const palette = useMemo(() => {
    const internal = tasks.filter(t => !hasExternal(t));
    return paletteFor(colourBy === 'team' ? internal.map(t => t.team_name) : internal.map(t => assigneeLabel(t)));
  }, [tasks, colourBy]);
  const colourOf = (t) => {
    if (t.is_summary) return '#475569';
    if (hasExternal(t)) return EXTERNAL_BAR.fill;
    const key = colourBy === 'team' ? t.team_name : assigneeLabel(t);
    return palette[key] || '#94A3B8';
  };

  /* ── Scroll sync ── */
  const syncFrom = (source, target) => (e) => {
    const el = e.target;
    setScrollTop(el.scrollTop);
    if (el.clientHeight && el.clientHeight !== viewportH) setViewportH(el.clientHeight);
    if (syncing.current) return;
    syncing.current = true;
    if (target.current) target.current.scrollTop = el.scrollTop;
    requestAnimationFrame(() => { syncing.current = false; });
  };

  // Measure once mounted, and again when the window changes, so the virtual
  // window and the "fit" width are both working from real numbers.
  useEffect(() => {
    const measure = () => {
      const el = rightRef.current;
      if (!el) return;
      if (el.clientHeight) setViewportH(el.clientHeight);
      if (el.clientWidth) setContainerW(el.clientWidth);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [fullScreen, density]);

  /* ── Getting somewhere ──
     Over two years, scrolling to find a date is hopeless. */
  const scrollToDate = useCallback((iso) => {
    const el = rightRef.current;
    if (!el || !iso) return;
    const x = xOf(iso);
    if (x == null) return;
    el.scrollLeft = Math.max(0, x - el.clientWidth / 3);
  }, [xOf]);

  const scrollToRow = useCallback((taskId) => {
    const i = rowIndex[taskId];
    if (i === undefined || !rightRef.current) return;
    rightRef.current.scrollTop = Math.max(0, i * rowH - rightRef.current.clientHeight / 3);
  }, [rowIndex, rowH]);

  // Open on today rather than at the beginning of time.
  const scrolledOnce = useRef(false);
  useEffect(() => {
    if (scrolledOnce.current || !rightRef.current || !hasDates) return;
    const x = xOf(today);
    if (x != null) {
      rightRef.current.scrollLeft = Math.max(0, x - rightRef.current.clientWidth / 3);
      scrolledOnce.current = true;
    }
  }, [today, xOf, hasDates]);

  /* ── Drag: move a bar, or drag its right edge to change duration ── */
  useEffect(() => {
    if (!drag) return;
    const onMove = (e) => {
      const delta = Math.round((e.clientX - drag.clientX) / pxPerDay);
      setDrag(d => (d && d.delta !== delta ? { ...d, delta } : d));
    };
    const onUp = () => {
      const { task, mode, delta } = drag;
      setDrag(null);
      if (!delta || !onRescheduleTask) return;
      if (mode === 'move') {
        onRescheduleTask(task.id, { start_date: addDays(task.start_date, delta) });
      } else {
        const span = (daysBetween(task.start_date, task.end_date) || 0) + 1 + delta;
        if (span >= 1) onRescheduleTask(task.id, { end_date: addDays(task.start_date, span - 1) });
      }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [drag, pxPerDay, onRescheduleTask]);

  const startDrag = (e, task, mode) => {
    if (!task.start_date || task.is_summary) return;   // a summary follows its children
    if (!onRescheduleTask) return;                    // read-only: nothing to drag to
    e.preventDefault(); e.stopPropagation();
    setDrag({ task, mode, clientX: e.clientX, delta: 0 });
  };

  useEffect(() => {
    if (!fullScreen) return;
    const onKey = (e) => { if (e.key === 'Escape') setFullScreen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [fullScreen]);

  /* ── Export ── */
  const exportImage = async (format) => {
    const svg = svgRef.current;
    if (!svg) return;
    const clone = svg.cloneNode(true);
    clone.setAttribute('width', totalW);
    clone.setAttribute('height', totalH + HDR_H);
    clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    // White ground, or a PNG of the chart is transparent and unreadable in a pack.
    const bg = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    bg.setAttribute('width', '100%'); bg.setAttribute('height', '100%'); bg.setAttribute('fill', '#ffffff');
    clone.insertBefore(bg, clone.firstChild);
    const source = new XMLSerializer().serializeToString(clone);
    const blob = new Blob([source], { type: 'image/svg+xml;charset=utf-8' });

    if (format === 'svg') return downloadBlob(blob, 'plan.svg');

    const url = URL.createObjectURL(blob);
    try {
      const img = new Image();
      await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = url; });
      const scale = 2;
      const canvas = document.createElement('canvas');
      canvas.width = totalW * scale;
      canvas.height = (totalH + HDR_H) * scale;
      const ctx = canvas.getContext('2d');
      ctx.scale(scale, scale);
      ctx.drawImage(img, 0, 0);
      canvas.toBlob(b => b && downloadBlob(b, 'plan.png'), 'image/png');
    } finally {
      URL.revokeObjectURL(url);
    }
  };

  if (!tasks.length) {
    return (
      <div className="bg-white border border-gray-200 rounded p-10 text-center text-[10px] text-gray-400">
        No tasks in this plan yet.
      </div>
    );
  }

  // When fitting, the granularity has to follow the width the plan was squeezed
  // into. Keeping the previously chosen level would draw a tick per day across
  // a three-year plan at half a pixel each.
  const z = fitWidth
    ? ([...ZOOM_LEVELS].reverse().find(l => l.px <= fitWidth) || ZOOM_LEVELS[ZOOM_LEVELS.length - 1])
    : ZOOM_MAP[zoom];
  const majorTicks = ticksFor(z.major, rangeFrom, rangeTo);
  const minorTicks = ticksFor(z.minor, rangeFrom, rangeTo);
  const showMinorLabels = pxPerDay * (z.minor === 'day' ? 1 : z.minor === 'week' ? 7 : z.minor === 'month' ? 30 : z.minor === 'quarter' ? 91 : 365) > 26;
  const todayX = xOf(today);

  // Non-working days, shaded, but only when a day is wide enough to see.
  const nonWorking = [];
  if (pxPerDay >= 5) {
    for (let i = 0; i < totalDays; i++) {
      const iso = addDays(rangeFrom, i);
      const d = dow(iso);
      if (d === 0 || d === 6 || holidaySet.has(iso)) nonWorking.push({ x: i * pxPerDay, holiday: holidaySet.has(iso) });
    }
  }

  const barGeom = (t) => {
    const i = rowIndex[t.id];
    if (i === undefined || !t.start_date) return null;
    const dragging = drag?.task.id === t.id;
    const shift = dragging && drag.mode === 'move' ? drag.delta * pxPerDay : 0;
    const stretch = dragging && drag.mode === 'resize' ? drag.delta * pxPerDay : 0;
    const x1 = xOf(t.start_date) + shift;
    const x2 = Math.max(x1 + Math.max(2, pxPerDay * 0.6), (xEnd(t.end_date) ?? xOf(t.start_date)) + shift + stretch);
    return { x1, x2, y: i * rowH + rowH / 2, i };
  };

  return (
    <div className={`bg-white border border-gray-200 rounded overflow-hidden flex flex-col ${
        fullScreen ? 'fixed inset-2 z-50 shadow-2xl' : ''}`}
         style={{ userSelect: drag ? 'none' : 'auto' }}>

      {/* ── Toolbar ── */}
      <div className="flex items-center gap-2 px-2 py-1.5 border-b border-gray-200 bg-gray-50 flex-wrap">
        <span className="text-[8px] font-bold uppercase tracking-wider text-gray-400">Timescale</span>
        <div className="flex rounded border border-gray-200 overflow-hidden">
          {ZOOM_LEVELS.map(lvl => (
            <button
              key={lvl.id}
              onClick={() => { setFitWidth(null); setZoom(lvl.id); }}
              className={`px-2 py-0.5 text-[9px] font-semibold transition-colors ${
                !fitWidth && zoom === lvl.id ? 'bg-navy text-white' : 'bg-white text-gray-500 hover:bg-gray-100'}`}
            >{lvl.label}</button>
          ))}
          <button
            onClick={applyFit}
            title="Fit the whole plan into the window"
            className={`px-2 py-0.5 text-[9px] font-semibold border-l border-gray-200 transition-colors ${
              fitWidth ? 'bg-navy text-white' : 'bg-white text-gray-500 hover:bg-gray-100'}`}
          >Fit</button>
        </div>

        <div className="w-px h-4 bg-gray-200 mx-1" />

        {[
          ['showBaseline', 'Baseline'],
          ['showCritical', 'Critical path'],
          ['showArrows',   'Links'],
          ['showProgress', 'Progress'],
          ['showFloat',    'Float'],
        ].map(([key, label]) => (
          <label key={key} className="flex items-center gap-1 cursor-pointer select-none">
            <input
              type="checkbox"
              className="w-3 h-3 accent-navy"
              checked={!!options[key]}
              onChange={e => onOptionsChange?.({ ...options, [key]: e.target.checked })}
            />
            <span className="text-[9px] text-gray-600">{label}</span>
          </label>
        ))}

        <div className="w-px h-4 bg-gray-200 mx-1" />

        {/* Getting somewhere. Over two years, scrolling to find a date is hopeless. */}
        <span className="text-[8px] font-bold uppercase tracking-wider text-gray-400">Go to</span>
        <div className="flex rounded border border-gray-200 overflow-hidden">
          <button onClick={() => scrollToDate(today)}
                  className="px-1.5 py-0.5 text-[9px] font-semibold bg-white text-gray-500 hover:bg-gray-100">Today</button>
          <button onClick={() => scrollToDate(dates.slice().sort()[0])}
                  className="px-1.5 py-0.5 text-[9px] font-semibold bg-white text-gray-500 hover:bg-gray-100 border-l border-gray-200">Start</button>
          <button onClick={() => scrollToDate(dates.slice().sort().slice(-1)[0])}
                  className="px-1.5 py-0.5 text-[9px] font-semibold bg-white text-gray-500 hover:bg-gray-100 border-l border-gray-200">Finish</button>
        </div>
        <input type="date" title="Jump to a date"
               onChange={e => e.target.value && scrollToDate(e.target.value)}
               className="text-[9px] border border-gray-200 rounded px-1 py-0.5 bg-white text-gray-600 w-[110px]" />

        <select value={density} onChange={e => setDensity(e.target.value)}
                title="Row height. A long plan fits on one screen at compact."
                className="text-[9px] border border-gray-200 rounded px-1 py-0.5 bg-white text-gray-600">
          <option value="comfortable">Comfortable</option>
          <option value="normal">Normal</option>
          <option value="compact">Compact</option>
        </select>

        <div className="ml-auto flex items-center gap-1">
          <span className="text-[8px] text-gray-400 mr-1">{visible.length} rows</span>
          <button onClick={() => setFullScreen(f => !f)}
                  title={fullScreen ? 'Back into the page' : 'Fill the window'}
                  className="px-2 py-0.5 text-[9px] font-semibold border border-gray-200 rounded bg-white text-gray-600 hover:bg-gray-100">
            {fullScreen ? '⤡ Exit' : '⤢ Full screen'}
          </button>
          <button onClick={() => exportImage('png')} className="px-2 py-0.5 text-[9px] font-semibold border border-gray-200 rounded bg-white text-gray-600 hover:bg-gray-100">Export PNG</button>
          <button onClick={() => exportImage('svg')} className="px-2 py-0.5 text-[9px] font-semibold border border-gray-200 rounded bg-white text-gray-600 hover:bg-gray-100">SVG</button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden" style={{ maxHeight: fullScreen ? 'none' : '70vh' }}>
        {/* ── Left: the task list ── */}
        <div className="flex flex-col flex-shrink-0 border-r border-gray-200 bg-white" style={{ width: leftW }}>
          <div className="flex-shrink-0 bg-gray-50 border-b border-gray-200 flex items-end px-2 gap-2" style={{ height: HDR_H }}>
            <span className="text-[8px] font-bold text-gray-400 uppercase tracking-wider pb-1.5 flex-1">Task</span>
            <span className="text-[8px] font-bold text-gray-400 uppercase tracking-wider pb-1.5 w-14 text-right">Start</span>
            <span className="text-[8px] font-bold text-gray-400 uppercase tracking-wider pb-1.5 w-14 text-right">Finish</span>
          </div>
          <div ref={leftRef} className="flex-1 overflow-y-scroll" style={{ scrollbarWidth: 'none' }}
               onScroll={syncFrom(leftRef, rightRef)}>
            <div style={{ height: firstRow * rowH }} />
            {slice.map(t => (
              <div
                key={t.id}
                onClick={() => onEdit?.(t)}
                onMouseEnter={() => setHover(t.id)}
                onMouseLeave={() => setHover(null)}
                className={`flex items-center gap-1 px-2 border-b border-gray-100 cursor-pointer transition-colors ${
                  hover === t.id ? 'bg-gold/5' : hasExternal(t) && !t.is_summary ? 'bg-amber-50' : ''} ${t.status === 'completed' ? 'opacity-60' : ''}`}
                style={{ height: rowH, minHeight: rowH,
                  boxShadow: hasExternal(t) && !t.is_summary ? `inset 3px 0 0 ${EXTERNAL_BAR.fill}` : undefined }}
              >
                <span style={{ width: t.depth * 11 }} className="flex-shrink-0" />
                {t.has_children ? (
                  <button
                    onClick={e => { e.stopPropagation(); onToggleCollapse?.(t.id); }}
                    className="w-3 flex-shrink-0 text-[8px] text-gray-400 hover:text-navy"
                    title={collapsed[t.id] ? 'Expand' : 'Collapse'}
                  >{collapsed[t.id] ? '▶' : '▼'}</button>
                ) : <span className="w-3 flex-shrink-0" />}
                <span className="text-[7.5px] text-gray-300 font-mono tabular-nums flex-shrink-0 w-8">{t.wbs_code}</span>
                <span className={`text-[9px] truncate flex-1 ${
                  t.is_summary ? 'font-bold text-gray-700' : 'text-gray-700'} ${
                  t.status === 'completed' ? 'line-through text-gray-400' : ''}`}>
                  {!!t.is_milestone && <span className="text-gold mr-0.5">◆</span>}
                  {t.name}
                </span>
                {showCritical && t.is_critical ? <span className="text-[7px] text-red-500 font-bold flex-shrink-0" title="On the critical path">CP</span> : null}
                {hasExternal(t) && (
                  <span className="text-[7px] font-bold px-1 rounded bg-amber-500 text-white flex-shrink-0 max-w-[64px] truncate"
                        title={`External: ${externalLabel(t)}`}>{externalLabel(t) || 'EXT'}</span>
                )}
                <span className="text-[7.5px] text-gray-400 tabular-nums w-14 text-right flex-shrink-0">{fmtD(t.start_date)}</span>
                <span className="text-[7.5px] text-gray-400 tabular-nums w-14 text-right flex-shrink-0">{fmtD(t.end_date)}</span>
              </div>
            ))}
            <div style={{ height: Math.max(0, (visible.length - lastRow) * rowH) }} />
          </div>
        </div>

        {/* ── Right: the timeline ── */}
        <div ref={rightRef} className="flex-1 min-w-0 overflow-auto" onScroll={syncFrom(rightRef, leftRef)}>
          <div style={{ width: totalW, minWidth: totalW, position: 'relative' }}>
            <svg
              ref={svgRef}
              width={totalW}
              height={totalH + HDR_H}
              style={{ display: 'block' }}
              onMouseLeave={() => setHover(null)}
            >
              <defs>
                <marker id="gantt-arrow" markerWidth="7" markerHeight="7" refX="6" refY="3" orient="auto">
                  <path d="M0,0.5 L6,3 L0,5.5 Z" fill="#94A3B8" />
                </marker>
                <marker id="gantt-arrow-crit" markerWidth="7" markerHeight="7" refX="6" refY="3" orient="auto">
                  <path d="M0,0.5 L6,3 L0,5.5 Z" fill="#DC2626" />
                </marker>
                <pattern id="gantt-stripe" width="6" height="6" patternTransform="rotate(45)" patternUnits="userSpaceOnUse">
                  <rect width="6" height="6" fill="rgba(255,255,255,0)" />
                  <line x1="0" y1="0" x2="0" y2="6" stroke="rgba(255,255,255,0.35)" strokeWidth="2.5" />
                </pattern>
                {/* Hazard tape for work an outside organisation is doing */}
                <pattern id="gantt-external" width="8" height="8" patternTransform="rotate(-45)" patternUnits="userSpaceOnUse">
                  <line x1="0" y1="0" x2="0" y2="8" stroke={EXTERNAL_BAR.stripe} strokeOpacity="0.35" strokeWidth="3" />
                </pattern>
              </defs>

              {/* Non-working days */}
              {nonWorking.map((n, i) => (
                <rect key={i} x={n.x} y={HDR_H} width={pxPerDay} height={totalH}
                      className={n.holiday ? undefined : 'gantt-nonworking'}
                      fill={n.holiday ? 'rgba(239,68,68,0.055)' : 'rgba(15,23,42,0.035)'} />
              ))}

              {/* Header */}
              <rect className="gantt-hdr" x="0" y="0" width={totalW} height={HDR_H} fill="#F9FAFB" />
              {majorTicks.map((iso, i) => {
                const x = xOf(iso);
                const next = majorTicks[i + 1] ? xOf(majorTicks[i + 1]) : totalW;
                return (
                  <g key={`maj-${iso}`}>
                    <line className="gantt-grid-major" x1={x} y1={0} x2={x} y2={HDR_H + totalH} stroke="#E5E7EB" strokeWidth="1" />
                    {next - x > 34 && (
                      <text className="gantt-hdr-text" x={x + 4} y={15} fontSize="9" fontWeight="700" fill="#4B5563" fontFamily="Arial, sans-serif">
                        {tickLabel(z.major, iso, true)}
                      </text>
                    )}
                  </g>
                );
              })}
              {minorTicks.map(iso => {
                const x = xOf(iso);
                return (
                  <g key={`min-${iso}`}>
                    <line className="gantt-grid-minor" x1={x} y1={HDR_H / 2} x2={x} y2={HDR_H + totalH} stroke="#F1F5F9" strokeWidth="1" />
                    {showMinorLabels && (
                      <text x={x + 3} y={HDR_H - 5} fontSize="8" fill="#9CA3AF" fontFamily="Arial, sans-serif">
                        {tickLabel(z.minor, iso, false)}
                      </text>
                    )}
                  </g>
                );
              })}
              <line className="gantt-hdr-rule" x1="0" y1={HDR_H} x2={totalW} y2={HDR_H} stroke="#D1D5DB" strokeWidth="1" />

              {/* Row banding */}
              {slice.map((t) => { const i = rowIndex[t.id]; return (
                <rect key={`row-${t.id}`} x="0" y={HDR_H + i * rowH} width={totalW} height={rowH}
                      fill={hover === t.id ? 'rgba(161,114,61,0.07)' : (t.is_summary ? 'rgba(15,23,42,0.022)'
                        : hasExternal(t) ? EXTERNAL_BAR.tint : 'transparent')} />
              ); })}

              <g transform={`translate(0, ${HDR_H})`}>
                {/* Dependency arrows, under the bars so they never hide one */}
                {showArrows && visible.map(t => (t.predecessors || []).map(dep => {
                  const from = barGeom(tasks.find(x => x.id === dep.predecessor_id) || {});
                  const to = barGeom(t);
                  if (!from || !to) return null;
                  // A link between two rows both far off screen is invisible
                  // either way, and there can be hundreds of them.
                  if (!inWindow(from.i) && !inWindow(to.i)) return null;
                  const crit = showCritical && t.is_critical && tasks.find(x => x.id === dep.predecessor_id)?.is_critical;
                  return (
                    <path
                      key={`${dep.predecessor_id}-${t.id}`}
                      d={arrowPath(dep.dep_type || 'FS', from, to)}
                      fill="none"
                      stroke={crit ? '#DC2626' : '#94A3B8'}
                      strokeWidth={crit ? 1.4 : 1}
                      strokeOpacity={crit ? 0.9 : 0.55}
                      markerEnd={`url(#${crit ? 'gantt-arrow-crit' : 'gantt-arrow'})`}
                    />
                  );
                }))}

                {/* Baseline bars, sitting under each task */}
                {showBaseline && slice.map(t => {
                  const i = rowIndex[t.id];
                  if (!t.baseline_start || !t.baseline_finish) return null;
                  const x = xOf(t.baseline_start);
                  const w = Math.max(2, xEnd(t.baseline_finish) - x);
                  return (
                    <rect key={`bl-${t.id}`} x={x} y={i * rowH + rowH - 8} width={w} height={3.5}
                          rx="1.5" fill="#9CA3AF" fillOpacity="0.75">
                      <title>{`Baseline: ${fmtDY(t.baseline_start)} to ${fmtDY(t.baseline_finish)}`}</title>
                    </rect>
                  );
                })}

                {/* Bars */}
                {slice.map(t => {
                  const g = barGeom(t);
                  if (!g) return null;
                  const colour = colourOf(t);
                  const critical = showCritical && t.is_critical;
                  const w = g.x2 - g.x1;
                  const barH = 13;
                  const y = g.i * rowH + (rowH - barH) / 2;
                  const tip = [
                    t.name,
                    `${fmtDY(t.start_date)} to ${fmtDY(t.end_date)} (${fmtDuration(t.duration_days, t.duration_unit)})`,
                    assigneeLabel(t) && `Who: ${assigneeLabel(t)}`,
                    hasExternal(t) && `External: ${externalLabel(t)}`,
                    `${TASK_STATUS_LABELS[t.status] || t.status} — ${t.completion_pct || 0}% done`,
                    t.expected_pct != null && `Expected by today: ${t.expected_pct}%`,
                    t.finish_variance_days != null && `Baseline: ${fmtVariance(t.finish_variance_days)}`,
                    t.total_float_days != null && `Float: ${t.total_float_days}d`,
                  ].filter(Boolean).join('\n');

                  if (t.is_milestone) {
                    const mTag = hasExternal(t) ? externalLabel(t) : '';
                    const mTagW = mTag.length * 4.4 + 8;
                    return (
                      <g key={t.id} onClick={() => onEdit?.(t)}
                         onMouseEnter={() => setHover(t.id)} onMouseLeave={() => setHover(null)}
                         style={{ cursor: 'pointer' }}>
                        <MilestoneDiamond
                          x={g.x1} y={g.i * rowH + rowH / 2} size={13}
                          color={critical ? '#DC2626' : (t.status === 'completed' ? '#10B981' : '#A1723D')}
                          done={t.status === 'completed'} critical={critical}
                        />
                        {mTag && (
                          <g pointerEvents="none">
                            <rect x={g.x1 + 9} y={g.i * rowH + rowH / 2 - 5.5} width={mTagW} height="11" rx="2" fill={EXTERNAL_BAR.stripe} />
                            <text x={g.x1 + 13} y={g.i * rowH + rowH / 2 + 2.5} fontSize="7" fontWeight="700" fill="#fff"
                                  fontFamily="Arial, sans-serif">{mTag}</text>
                          </g>
                        )}
                        <text className="gantt-label" x={g.x1 + 10 + (mTag ? mTagW + 3 : 0)} y={g.i * rowH + rowH / 2 + 3} fontSize="8" fill="#4B5563" fontFamily="Arial, sans-serif">
                          {pxPerDay > 2 ? t.name : ''}
                        </text>
                        <title>{tip}</title>
                      </g>
                    );
                  }

                  if (t.is_summary) {
                    return (
                      <g key={t.id} onClick={() => onEdit?.(t)}
                         onMouseEnter={() => setHover(t.id)} onMouseLeave={() => setHover(null)}
                         style={{ cursor: 'pointer' }}>
                        <SummaryBracket x={g.x1} w={w} y={g.i * rowH + rowH / 2 - 5} color="#334155" critical={critical} />
                        <title>{tip}</title>
                      </g>
                    );
                  }

                  const pct = Math.min(100, Math.max(0, t.completion_pct || 0));
                  const behind = t.schedule_state === 'behind';
                  const external = hasExternal(t);
                  const tag = external ? externalLabel(t) : '';
                  const tagW = tag.length * 4.4 + 8;
                  const tagX = g.x2 + (behind && pxPerDay > 3 ? 11 : 4);
                  return (
                    <g key={t.id}
                       onMouseEnter={() => setHover(t.id)} onMouseLeave={() => setHover(null)}
                       onMouseDown={e => startDrag(e, t, 'move')}
                       onClick={e => { if (!drag?.delta) { e.stopPropagation(); onEdit?.(t); } }}
                       style={{ cursor: !onRescheduleTask ? 'default' : drag ? 'grabbing' : 'grab' }}>
                      <rect x={g.x1} y={y} width={w} height={barH} rx="2.5"
                            fill={colour} fillOpacity={t.status === 'completed' ? 0.45 : 0.9}
                            stroke={critical ? '#DC2626' : external ? EXTERNAL_BAR.edge : 'none'}
                            strokeWidth={critical ? 1.3 : external ? 1.2 : 0}
                            strokeDasharray={external && !critical ? '3 2' : undefined} />
                      {external && (
                        <rect x={g.x1} y={y} width={w} height={barH} rx="2.5" fill="url(#gantt-external)" pointerEvents="none" />
                      )}
                      {/* Progress: how much of the bar is actually done */}
                      {showProgress && pct > 0 && (
                        <rect x={g.x1} y={y + 3.5} width={Math.max(1.5, (w * pct) / 100)} height={barH - 7}
                              rx="1.5" fill="#0F172A" fillOpacity="0.42" />
                      )}
                      {t.status === 'blocked' && (
                        <rect x={g.x1} y={y} width={w} height={barH} rx="2.5" fill="url(#gantt-stripe)" />
                      )}
                      {/* Float, so you can see what has room and what has none */}
                      {showFloat && t.total_float_days > 0 && (
                        <line x1={g.x2} y1={g.i * rowH + rowH / 2} x2={g.x2 + t.total_float_days * pxPerDay}
                              y2={g.i * rowH + rowH / 2} stroke={colour} strokeWidth="1" strokeDasharray="2 2" strokeOpacity="0.6" />
                      )}
                      {behind && pxPerDay > 3 && (
                        <circle cx={g.x2 + 5} cy={g.i * rowH + rowH / 2} r="2.5" fill="#DC2626">
                          <title>Behind where it should be by today</title>
                        </circle>
                      )}
                      {/* Who it is waiting on, beside the bar, so it reads at any zoom */}
                      {tag && (
                        <g pointerEvents="none">
                          <rect x={tagX} y={y + 1} width={tagW} height={barH - 2} rx="2" fill={EXTERNAL_BAR.stripe} />
                          <text x={tagX + 4} y={y + barH - 3.5} fontSize="7" fontWeight="700" fill="#fff"
                                fontFamily="Arial, sans-serif">{tag}</text>
                        </g>
                      )}
                      {/* Right-edge grab handle for duration */}
                      <rect x={g.x2 - 4} y={y} width="6" height={barH} fill="transparent"
                            style={{ cursor: onRescheduleTask ? 'ew-resize' : 'default' }}
                            onMouseDown={e => startDrag(e, t, 'resize')} />
                      {w > 44 && (
                        <text x={g.x1 + 5} y={y + barH - 3.5} fontSize="8" fill="#fff" fontWeight="600"
                              fontFamily="Arial, sans-serif" pointerEvents="none"
                              style={{ paintOrder: 'stroke', stroke: 'rgba(0,0,0,0.25)', strokeWidth: 1.6 }}>
                          {clipText(t.name, w)}
                        </text>
                      )}
                      <title>{tip}</title>
                    </g>
                  );
                })}
              </g>

              {/* Today */}
              {todayX != null && todayX >= 0 && (
                <g>
                  <line x1={todayX} y1={HDR_H - 10} x2={todayX} y2={HDR_H + totalH}
                        stroke="#DC2626" strokeWidth="1.4" strokeDasharray="4 3" />
                  <rect x={todayX - 15} y={HDR_H - 20} width="30" height="11" rx="2" fill="#DC2626" />
                  <text x={todayX} y={HDR_H - 12} fontSize="7.5" fill="#fff" fontWeight="700"
                        textAnchor="middle" fontFamily="Arial, sans-serif">TODAY</text>
                </g>
              )}
            </svg>
          </div>
        </div>
      </div>

      {/* ── Legend ── */}
      <div className="flex items-center gap-3 px-3 py-1.5 border-t border-gray-100 bg-gray-50 flex-wrap text-[8px] text-gray-500">
        <span className="flex items-center gap-1"><svg width="14" height="8"><path className="gantt-summary" d="M0,0 L14,0 L14,7 L11,5 L3,5 L0,7 Z" fill="#334155" /></svg> Summary</span>
        <span className="flex items-center gap-1"><svg width="10" height="10"><path d="M5,0 L10,5 L5,10 L0,5 Z" fill="#A1723D" /></svg> Milestone</span>
        <span className="flex items-center gap-1"><span className="inline-block w-3 h-1 rounded-sm" style={{ background: '#9CA3AF' }} /> Baseline</span>
        {showCritical && <span className="flex items-center gap-1"><span className="inline-block w-3 h-2 rounded-sm border border-red-500" /> Critical path</span>}
        <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full bg-red-600" /> Behind today</span>
        <span className="flex items-center gap-1">
          <svg width="16" height="9">
            <rect x="0.5" y="0.5" width="15" height="8" rx="1.5" fill={EXTERNAL_BAR.fill} stroke={EXTERNAL_BAR.edge} strokeDasharray="3 2" />
            <rect x="0.5" y="0.5" width="15" height="8" rx="1.5" fill="url(#gantt-external)" />
          </svg>
          External
        </span>
        {Object.entries(palette).slice(0, 6).map(([name, c]) => (
          <span key={name} className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: c }} /> {name}</span>
        ))}
        <span className="ml-auto text-gray-400">Drag a bar to move it, drag its right edge to change duration.</span>
      </div>
    </div>
  );
}

function clipText(text, width) {
  const max = Math.floor((width - 8) / 4.6);
  if (!text) return '';
  return text.length > max ? `${text.slice(0, Math.max(1, max - 1))}…` : text;
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
